/**
 * components/TradingHub/utils/sessionDraftUtils.ts
 * Runtime utilities for SessionDraft
 *
 * Contains factory functions and helpers for SessionDraft.
 * Separated from type definitions per CODING_STANDARDS (src/types/ is type-only).
 */

// Type imports
import type { SessionDraft, PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { DraftMetadata } from '@/types/transient/DraftMetadata';
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type { PreTradingSettings, PreTradingSectionConfig } from '@/types/schema/PreTradingSettings';
import type { SnapshotFieldDefinition } from '@/types/schema/MarketSnapshotSettings';
import type { Instrument } from '@/types/schema/Instrument';
import type { InstrumentSnapshot } from '@/types/shared/InstrumentSnapshot';
import type { DropdownOption } from '@/types/shared';
import type { TradingSetupDefaults, StrategyPlaybookPair } from '@/types/shared/TradingSetupTypes';
import type { TradingSetupSettings } from '@/types/schema/TradingSetupSettings';

// Runtime imports
import { generateUUID } from '@/utils/uuid';
import { PRE_TRADING_SECTION_IDS, normalizeFixedSections } from '@/constants/preTrading';

// ===== SECTION KEY TYPE =====

/**
 * Section keys for granular divergence detection in SettingsChangedModal.
 * Each section can be independently detected and resolved (apply new vs keep current).
 */
export type SectionKey = 'healthMetrics' | 'personalPrep' | 'sectionVisibility' | 'snapshotFields' | 'finalCheckItems' | 'tradingSetup' | 'riskProfileDefault';

/**
 * Creates a deep copy of PreTradingSettings fields needed for config snapshot.
 * Ensures the snapshot is independent of the original settings object.
 *
 * Also used by SessionDraftService.updatePreTradingConfig() for config updates.
 *
 * @param settings - The PreTradingSettings to snapshot
 * @returns A deep-copied PreTradingConfigSnapshot
 */
export function buildPreTradingConfigSnapshot(settings: PreTradingSettings): PreTradingConfigSnapshot {
  const config: PreTradingConfigSnapshot = {
    healthMetrics: {
      source: settings.healthMetrics.source,
      metrics: settings.healthMetrics.metrics.map(m => ({ ...m })),
    },
    personalPrepFields: settings.personalPrepFields.map(f => {
      // Deep copy field, only including options if defined (exactOptionalPropertyTypes)
      const field: PreTradingConfigSnapshot['personalPrepFields'][number] = {
        id: f.id,
        name: f.name,
        type: f.type,
      };
      if (f.description !== undefined) field.description = f.description;
      if (f.options) field.options = f.options.map(o => ({ ...o }));
      return field;
    }),
    // NOTE: snapshotFields removed (v11) - market snapshot field definitions
    // are captured per-observation in MarketStateDraft.fieldConfigSnapshot
    // NOTE: economicImpactAsset removed - economic calendar now uses EconomicCalendarSettings
    finalCheckItems: settings.finalCheckItems.map(item => ({ ...item })),
    maxObjectives: settings.maxObjectives,
  };

  // Only include sectionConfig if defined AND non-empty (empty = use defaults)
  if (settings.sectionConfig?.length) {
    config.sectionConfig = settings.sectionConfig.map(s => ({ ...s }));
  }

  return config;
}

// ===== CONFIG MISMATCH DETECTION =====

/**
 * Compares two arrays of dropdown options for mismatches.
 * Order-insensitive comparison by `id`. A label change for the same `id` is a mismatch.
 *
 * @param snapshotOptions - Options from the draft's config snapshot (may be undefined)
 * @param currentOptions - Options from current settings (may be undefined)
 * @returns true if there is a mismatch, false if they match
 */
function hasDropdownOptionsMismatch(
  snapshotOptions: DropdownOption[] | undefined,
  currentOptions: DropdownOption[] | undefined
): boolean {
  // Normalize undefined to empty array for comparison
  const snapshot = snapshotOptions ?? [];
  const current = currentOptions ?? [];

  // Different count = mismatch
  if (snapshot.length !== current.length) {
    return true;
  }

  // Build map of current options by id
  const currentById = new Map(current.map(o => [o.id, o]));

  // Check each snapshot option exists in current with same label
  for (const snapshotOption of snapshot) {
    const currentOption = currentById.get(snapshotOption.id);
    if (!currentOption) {
      // Option was removed
      return true;
    }
    if (snapshotOption.label !== currentOption.label) {
      // Label changed for same id
      return true;
    }
  }

  return false;
}

/**
 * Compares health metrics configuration for mismatches.
 * Order-insensitive comparison by metric `id`.
 *
 * @param snapshotMetrics - Health metrics from the draft's config snapshot
 * @param currentMetrics - Health metrics from current settings
 * @returns true if there is a mismatch, false if they match
 */
function hasHealthMetricsMismatch(
  snapshotMetrics: PreTradingConfigSnapshot['healthMetrics'],
  currentMetrics: PreTradingSettings['healthMetrics']
): boolean {
  // Source change = mismatch
  if (snapshotMetrics.source !== currentMetrics.source) {
    return true;
  }

  const snapshotList = snapshotMetrics.metrics;
  const currentList = currentMetrics.metrics;

  // Different count = mismatch
  if (snapshotList.length !== currentList.length) {
    return true;
  }

  // Build map of current metrics by id
  const currentById = new Map(currentList.map(m => [m.id, m]));

  // Check each snapshot metric exists in current with same properties
  for (const snapshotMetric of snapshotList) {
    const currentMetric = currentById.get(snapshotMetric.id);
    if (!currentMetric) {
      // Metric was removed
      return true;
    }
    // Compare properties: name, min, max, unit
    if (
      snapshotMetric.name !== currentMetric.name ||
      snapshotMetric.min !== currentMetric.min ||
      snapshotMetric.max !== currentMetric.max ||
      snapshotMetric.unit !== currentMetric.unit
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Compares personal prep field configurations for mismatches.
 * Order-insensitive comparison by field `id`.
 *
 * @param snapshotFields - Personal prep fields from the draft's config snapshot
 * @param currentFields - Personal prep fields from current settings
 * @returns true if there is a mismatch, false if they match
 */
function hasPersonalPrepFieldsMismatch(
  snapshotFields: PreTradingConfigSnapshot['personalPrepFields'],
  currentFields: PreTradingSettings['personalPrepFields']
): boolean {
  // Different count = mismatch
  if (snapshotFields.length !== currentFields.length) {
    return true;
  }

  // Build map of current fields by id
  const currentById = new Map(currentFields.map(f => [f.id, f]));

  // Check each snapshot field exists in current with same properties
  for (const snapshotField of snapshotFields) {
    const currentField = currentById.get(snapshotField.id);
    if (!currentField) {
      // Field was removed
      return true;
    }
    // Compare properties: name, type, description
    if (
      snapshotField.name !== currentField.name ||
      snapshotField.type !== currentField.type ||
      snapshotField.description !== currentField.description
    ) {
      return true;
    }
    // For dropdown fields, also compare options
    if (hasDropdownOptionsMismatch(snapshotField.options, currentField.options)) {
      return true;
    }
  }

  return false;
}

/**
 * Normalizes a sectionConfig array (or undefined) into a fully expanded, fixed-section-pinned
 * array suitable for comparison or hashing. Mirrors the logic in `deriveWorkflowSections()` +
 * `normalizeFixedSections()`: builds defaults for all known section IDs, merges with input
 * config, and normalizes fixed sections. Ensures that an un-normalized settings config (missing
 * tradingSetup/finalChecks) compares equal to a normalized draft config that includes them.
 */
function normalizeSectionConfigForComparison(
  config: PreTradingSectionConfig[] | undefined
): PreTradingSectionConfig[] {
  // Build defaults for all sections
  const defaults = PRE_TRADING_SECTION_IDS.map((id, index) => ({
    id,
    visible: true,
    order: index,
  }));

  // Merge: use config values where they exist, defaults for missing sections
  const validIds = new Set<string>(PRE_TRADING_SECTION_IDS);
  const merged = defaults.map((defaultItem) => {
    const existing = config?.find(
      (c) => c.sectionId === defaultItem.id && validIds.has(c.sectionId)
    );
    return existing
      ? { id: existing.sectionId, visible: existing.visible, order: existing.order }
      : defaultItem;
  });

  // Normalize: inject missing non-optional sections, force visibility, pin fixed sections
  const normalized = normalizeFixedSections(merged);

  // Convert back to PreTradingSectionConfig shape (sectionId field name)
  return normalized.map((s) => ({
    sectionId: s.id,
    visible: s.visible,
    order: s.order,
  }));
}

/**
 * Compares section config arrays for mismatches in visibility or order.
 *
 * @param snapshotConfig - The draft's sectionConfig (may be undefined for legacy drafts)
 * @param currentConfig - The current settings' sectionConfig (may be undefined)
 * @returns true if there is a mismatch, false if configs match
 */
function hasSectionConfigMismatch(
  snapshotConfig: PreTradingSectionConfig[] | undefined,
  currentConfig: PreTradingSectionConfig[] | undefined
): boolean {
  // Normalize both sides so implicit defaults (missing sections) match explicit normalized configs
  const normalizedSnapshot = normalizeSectionConfigForComparison(snapshotConfig);
  const normalizedCurrent = normalizeSectionConfigForComparison(currentConfig);

  // After normalization, both arrays have the same set of section IDs
  if (normalizedSnapshot.length !== normalizedCurrent.length) {
    return true;
  }

  // Build map of current sections by id
  const currentById = new Map(normalizedCurrent.map((s) => [s.sectionId, s]));

  // Check each snapshot section exists in current with same visibility and order
  for (const snapshotSection of normalizedSnapshot) {
    const currentSection = currentById.get(snapshotSection.sectionId);
    if (!currentSection) {
      return true;
    }
    if (
      snapshotSection.visible !== currentSection.visible ||
      snapshotSection.order !== currentSection.order
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Compares final check items configuration for mismatches.
 * Order-insensitive comparison by item `id`.
 *
 * @param snapshotItems - Final check items from the draft's config snapshot (may be undefined)
 * @param currentItems - Final check items from current settings (may be undefined)
 * @returns true if there is a mismatch, false if they match
 */
function hasFinalCheckItemsMismatch(
  snapshotItems: Array<{ id: string; label: string }> | undefined,
  currentItems: Array<{ id: string; label: string }> | undefined
): boolean {
  // Normalize undefined to empty array
  const snapshot = snapshotItems ?? [];
  const current = currentItems ?? [];

  // Different count = mismatch
  if (snapshot.length !== current.length) {
    return true;
  }

  // Build map of current items by id
  const currentById = new Map(current.map(item => [item.id, item]));

  // Check each snapshot item exists in current with same label
  for (const snapshotItem of snapshot) {
    const currentItem = currentById.get(snapshotItem.id);
    if (!currentItem) {
      // Item was removed
      return true;
    }
    if (snapshotItem.label !== currentItem.label) {
      // Label changed for same id
      return true;
    }
  }

  return false;
}

/**
 * Compares a draft's config snapshot against current settings to detect mismatches.
 * Compares healthMetrics, personalPrepFields, and sectionConfig.
 *
 * **Contract**: Caller must ensure `draftConfig` is defined. Legacy drafts without
 * a config snapshot should have the fallback applied before calling this function.
 *
 * @param draftConfig - The draft's PreTradingConfigSnapshot
 * @param currentSettings - The current PreTradingSettings
 * @returns true if there is a mismatch, false if configs match
 */
export function hasPreTradingConfigMismatch(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  // Check health metrics mismatch
  if (hasHealthMetricsMismatch(draftConfig.healthMetrics, currentSettings.healthMetrics)) {
    return true;
  }

  // Check personal prep fields mismatch
  if (hasPersonalPrepFieldsMismatch(draftConfig.personalPrepFields, currentSettings.personalPrepFields)) {
    return true;
  }

  // Check section config mismatch (visibility and order)
  if (hasSectionConfigMismatch(draftConfig.sectionConfig, currentSettings.sectionConfig)) {
    return true;
  }

  return false;
}

/**
 * Compares only the health metrics section of a draft's config snapshot against current settings.
 * Used for per-section checkbox visibility in workflow config modals.
 *
 * **Contract**: Caller must ensure `draftConfig` is defined. Legacy drafts without
 * a config snapshot should have the fallback applied before calling this function.
 *
 * @param draftConfig - The draft's PreTradingConfigSnapshot
 * @param currentSettings - The current PreTradingSettings
 * @returns true if health metrics config has a mismatch, false if they match
 */
export function hasHealthMetricsConfigMismatch(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  return hasHealthMetricsMismatch(draftConfig.healthMetrics, currentSettings.healthMetrics);
}

/**
 * Compares only the personal prep fields section of a draft's config snapshot against current settings.
 * Used for per-section checkbox visibility in workflow config modals.
 *
 * **Contract**: Caller must ensure `draftConfig` is defined. Legacy drafts without
 * a config snapshot should have the fallback applied before calling this function.
 *
 * @param draftConfig - The draft's PreTradingConfigSnapshot
 * @param currentSettings - The current PreTradingSettings
 * @returns true if personal prep fields config has a mismatch, false if they match
 */
export function hasPersonalPrepFieldsConfigMismatch(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  return hasPersonalPrepFieldsMismatch(draftConfig.personalPrepFields, currentSettings.personalPrepFields);
}

/**
 * Compares only the section visibility & order config of a draft's config snapshot against current settings.
 * Used for per-section divergence detection in SettingsChangedModal.
 *
 * **Contract**: Caller must ensure `draftConfig` is defined. Legacy drafts without
 * a config snapshot should have the fallback applied before calling this function.
 *
 * @param draftConfig - The draft's PreTradingConfigSnapshot
 * @param currentSettings - The current PreTradingSettings
 * @returns true if section visibility/order config has a mismatch, false if they match
 */
export function hasSectionVisibilityConfigMismatch(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  return hasSectionConfigMismatch(draftConfig.sectionConfig, currentSettings.sectionConfig);
}

/**
 * Compares only the final check items section of a draft's config snapshot against current settings.
 * Used for per-section divergence detection in SettingsChangedModal.
 *
 * **Contract**: Caller must ensure `draftConfig` is defined. Legacy drafts without
 * a config snapshot should have the fallback applied before calling this function.
 *
 * @param draftConfig - The draft's PreTradingConfigSnapshot
 * @param currentSettings - The current PreTradingSettings
 * @returns true if final check items config has a mismatch, false if they match
 */
export function hasFinalCheckItemsConfigMismatch(
  draftConfig: PreTradingConfigSnapshot,
  currentSettings: PreTradingSettings
): boolean {
  return hasFinalCheckItemsMismatch(draftConfig.finalCheckItems, currentSettings.finalCheckItems);
}

/**
 * Compares a draft's market snapshot field config against current settings to detect mismatches.
 * Order-insensitive comparison by field ID.
 *
 * Used to determine if the SettingsChangedModal should appear for snapshot fields divergence.
 * The first marketState's fieldConfigSnapshot is used as canonical (all observations in a session
 * share the same field config at creation time).
 *
 * @param draftFieldConfig - The draft's fieldConfigSnapshot from MarketStateDraft (may be undefined)
 * @param currentFields - The current SnapshotFieldDefinition array from MarketSnapshotSettings
 * @returns true if there is a mismatch, false if configs match
 */
export function hasSnapshotFieldsConfigMismatch(
  draftFieldConfig: MarketStateDraft['fieldConfigSnapshot'] | undefined,
  currentFields: SnapshotFieldDefinition[]
): boolean {
  // Normalize undefined to empty array
  const snapshot = draftFieldConfig ?? [];

  // Both empty = no mismatch
  if (snapshot.length === 0 && currentFields.length === 0) {
    return false;
  }

  // Draft has no config but settings have fields = mismatch (settings added)
  if (snapshot.length === 0 && currentFields.length > 0) {
    return true;
  }

  // Draft has config but settings empty = mismatch (settings removed)
  if (snapshot.length > 0 && currentFields.length === 0) {
    return true;
  }

  // Different count = mismatch
  if (snapshot.length !== currentFields.length) {
    return true;
  }

  // Build map of current fields by id
  const currentById = new Map(currentFields.map(f => [f.id, f]));

  // Check each snapshot field exists in current with same properties
  for (const snapshotField of snapshot) {
    const currentField = currentById.get(snapshotField.fieldId);
    if (!currentField) {
      // Field was removed
      return true;
    }
    // Compare properties: name, type, description, pinToLive
    if (
      snapshotField.name !== currentField.name ||
      snapshotField.type !== currentField.type ||
      snapshotField.description !== currentField.description ||
      snapshotField.pinToLive !== currentField.pinToLive
    ) {
      return true;
    }
    // For dropdown fields, also compare options
    if (hasDropdownOptionsMismatch(snapshotField.options, currentField.options)) {
      return true;
    }
  }

  return false;
}

// ===== TRADING SETUP CONFIG MISMATCH =====

/**
 * Comparator for sorting StrategyPlaybookPair arrays.
 * Uses strategyId as primary sort, playbookId as tiebreaker.
 */
function comparePairs(
  a: { strategyId: string; playbookId: string },
  b: { strategyId: string; playbookId: string }
): number {
  return a.strategyId.localeCompare(b.strategyId) || a.playbookId.localeCompare(b.playbookId);
}

// ===== PER-SECTION EQUALITY HELPERS =====

/**
 * Order-insensitive equality check for StrategyPlaybookPair arrays.
 * Sorts copies by strategyId then playbookId before element-wise comparison.
 *
 * @param a - First pair array
 * @param b - Second pair array
 * @returns true if arrays contain the same pairs (regardless of order)
 */
export function arePairsEqual(
  a: StrategyPlaybookPair[],
  b: StrategyPlaybookPair[]
): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort(comparePairs);
  const sortedB = [...b].sort(comparePairs);
  for (let i = 0; i < sortedA.length; i++) {
    const pa = sortedA[i];
    const pb = sortedB[i];
    if (!pa || !pb || pa.strategyId !== pb.strategyId || pa.playbookId !== pb.playbookId) {
      return false;
    }
  }
  return true;
}

/**
 * Order-insensitive equality check for string arrays.
 * Sorts copies alphabetically before element-wise comparison.
 *
 * @param a - First string array
 * @param b - Second string array
 * @returns true if arrays contain the same strings (regardless of order)
 */
export function areStringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return false;
  }
  return true;
}

/**
 * Compares a SessionDraft's trading setup fields against TradingSetupSettings defaults
 * to detect configuration mismatches. Checks exactly three field pairs:
 * - activePlaybookSelections vs defaultStrategyPlaybookPairs (order-insensitive)
 * - defaultAccountId vs defaultAccountId
 * - focusInstrumentIds vs defaultFocusInstrumentIds (order-insensitive)
 *
 * Explicitly excludes: plannedAccountIds (derived), riskSnapshot (risk-state),
 * setupConfirmed (runtime workflow state).
 *
 * **Contract**: The `settings` parameter must have `defaultAccountId` already resolved
 * to the effective value (single-account fallback applied) before calling. When
 * `TradingSetupSettings.defaultAccountId` is `undefined` and only one active account
 * exists, the caller must resolve it to that account's ID first. See PreTradingPhase.tsx
 * divergence detection (lines 1044-1056) and handleConfirmResolution (lines 2231-2241)
 * for the canonical resolution pattern.
 *
 * @param draft - The SessionDraft to check
 * @param settings - The current TradingSetupSettings defaults (with effective account ID resolved)
 * @returns true if there is a mismatch, false if they match
 */
export function hasTradingSetupConfigMismatch(
  draft: SessionDraft,
  settings: TradingSetupSettings
): boolean {
  if (!arePairsEqual(
    draft.activePlaybookSelections ?? [],
    settings.defaultStrategyPlaybookPairs
  )) {
    return true;
  }

  if (draft.defaultAccountId !== settings.defaultAccountId) {
    return true;
  }

  if (!areStringArraysEqual(
    draft.focusInstrumentIds ?? [],
    settings.defaultFocusInstrumentIds
  )) {
    return true;
  }

  return false;
}

// ===== TRADING SETUP DEFAULTS RESOLUTION =====

/** Identifies one of the three trading setup sections. */
export type TradingSetupSectionId = 'strategyPlaybookPairs' | 'account' | 'focusInstruments';

/** The action the system should take for a section. */
export type SectionResolutionDecision = 'ignore' | 'apply' | 'prompt';

/** The reason behind a section's resolution decision. */
export type SectionResolutionReason = 'unchanged' | 'already-matching' | 'aligned-with-old' | 'diverged';

/** Resolution outcome for a single section. */
export interface SectionResolution {
  decision: SectionResolutionDecision;
  reason: SectionResolutionReason;
}

/**
 * Input for the per-section resolution engine. Each of the three value sets
 * (old defaults, new defaults, current session) provides the same three fields.
 *
 * **Contract**: Account fields are named `effectiveAccountId` to enforce that
 * callers pass already-resolved values. The codebase resolves single-account
 * `undefined → accountId` at the call site (PreTradingPhase.tsx lines 1044-1056,
 * 2231-2241). The resolution engine must never operate on raw settings values.
 */
export interface TradingSetupResolutionInput {
  oldDefaults: {
    pairs: StrategyPlaybookPair[];
    effectiveAccountId: string | undefined;
    focusInstrumentIds: string[];
  };
  newDefaults: {
    pairs: StrategyPlaybookPair[];
    effectiveAccountId: string | undefined;
    focusInstrumentIds: string[];
  };
  currentSession: {
    pairs: StrategyPlaybookPair[];
    effectiveAccountId: string | undefined;
    focusInstrumentIds: string[];
  };
}

/** Resolution result for all three trading setup sections. */
export interface TradingSetupResolutionResult {
  strategyPlaybookPairs: SectionResolution;
  account: SectionResolution;
  focusInstruments: SectionResolution;
  /** True if any section has decision === 'prompt'. */
  hasPromptSections: boolean;
}

/**
 * Resolves a single section using the 4-step decision tree:
 * 1. Old default === new default? → ignore (unchanged)
 * 2. Current session === new default? → ignore (already-matching)
 * 3. Current session === old default? → apply silently (aligned-with-old)
 * 4. Else → prompt user (diverged)
 *
 * @param oldVal - The old default value
 * @param newVal - The new default value
 * @param currentVal - The current session value
 * @param isEqual - Equality comparator for this section's value type
 * @returns The resolution decision and reason
 */
function resolveSection<T>(
  oldVal: T,
  newVal: T,
  currentVal: T,
  isEqual: (a: T, b: T) => boolean
): SectionResolution {
  // Step 1: Did the default change?
  if (isEqual(oldVal, newVal)) {
    return { decision: 'ignore', reason: 'unchanged' };
  }

  // Step 2: Does the session already match the new default?
  if (isEqual(currentVal, newVal)) {
    return { decision: 'ignore', reason: 'already-matching' };
  }

  // Step 3: Was the session aligned with the old default?
  if (isEqual(currentVal, oldVal)) {
    return { decision: 'apply', reason: 'aligned-with-old' };
  }

  // Step 4: Session diverged from both old and new defaults
  return { decision: 'prompt', reason: 'diverged' };
}

/**
 * Evaluates per-section resolution for all three trading setup sections.
 * Applies the 4-step decision tree independently to strategy-playbook pairs,
 * account, and focus instruments.
 *
 * @param input - Old defaults, new defaults, and current session values
 * @returns Per-section resolution decisions with hasPromptSections flag
 */
export function resolveTradingSetupDefaults(
  input: TradingSetupResolutionInput
): TradingSetupResolutionResult {
  const strategyPlaybookPairs = resolveSection(
    input.oldDefaults.pairs,
    input.newDefaults.pairs,
    input.currentSession.pairs,
    arePairsEqual
  );

  const account = resolveSection(
    input.oldDefaults.effectiveAccountId,
    input.newDefaults.effectiveAccountId,
    input.currentSession.effectiveAccountId,
    (a, b) => a === b
  );

  const focusInstruments = resolveSection(
    input.oldDefaults.focusInstrumentIds,
    input.newDefaults.focusInstrumentIds,
    input.currentSession.focusInstrumentIds,
    areStringArraysEqual
  );

  const hasPromptSections =
    strategyPlaybookPairs.decision === 'prompt' ||
    account.decision === 'prompt' ||
    focusInstruments.decision === 'prompt';

  return { strategyPlaybookPairs, account, focusInstruments, hasPromptSections };
}

// ===== TRADING SETUP COMPLETION VALIDATION =====

/** The four fields needed to evaluate Trading Setup completion. */
export interface TradingSetupCompletionInput {
  activePlaybookSelections: StrategyPlaybookPair[];
  defaultAccountId: string | undefined;
  selectedRiskProfileId: string | undefined;
  setupConfirmed: boolean;
}

/** Identifies a specific Trading Setup completion criterion. */
export type TradingSetupCriterion = 'selections' | 'account' | 'riskProfile' | 'confirmation';

/** Structured result from Trading Setup completion validation. */
export interface TradingSetupCompletionStatus {
  hasSelections: boolean;
  hasAccount: boolean;
  hasRiskProfile: boolean;
  isConfirmed: boolean;
  isComplete: boolean;
  missingCriteria: TradingSetupCriterion[];
}

/**
 * Evaluates whether the Trading Setup step is complete.
 * Pure function — accepts narrow input (4 field values), returns structured result.
 *
 * Completion requires all four criteria:
 * 1. At least one (strategyId, playbookId) pair in activePlaybookSelections
 * 2. defaultAccountId is set (non-empty string)
 * 3. selectedRiskProfileId is set (non-empty string)
 * 4. setupConfirmed === true
 *
 * @param input - The four field values to evaluate
 * @returns Structured status with per-criterion booleans, overall isComplete, and missingCriteria list
 */
export function getTradingSetupCompletionStatus(
  input: TradingSetupCompletionInput
): TradingSetupCompletionStatus {
  const hasSelections = input.activePlaybookSelections.length > 0;
  const hasAccount = !!input.defaultAccountId;
  const hasRiskProfile = !!input.selectedRiskProfileId;
  const isConfirmed = input.setupConfirmed;

  const missingCriteria: TradingSetupCriterion[] = [];
  if (!hasSelections) missingCriteria.push('selections');
  if (!hasAccount) missingCriteria.push('account');
  if (!hasRiskProfile) missingCriteria.push('riskProfile');
  if (!isConfirmed) missingCriteria.push('confirmation');

  const isComplete = missingCriteria.length === 0;
  return { hasSelections, hasAccount, hasRiskProfile, isConfirmed, isComplete, missingCriteria };
}

// ===== SETTINGS HASH COMPUTATION =====

/**
 * Simple djb2 hash function for string input.
 * Non-cryptographic, deterministic hash suitable for change detection.
 */
function djb2Hash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and then to hex string
  return (hash >>> 0).toString(16);
}

/**
 * Computes a deterministic hash of healthMetrics config for per-section ack tracking.
 * Uses stable ID ordering to ensure consistent hash regardless of array order.
 *
 * @param settings - The PreTradingSettings containing healthMetrics to hash
 * @returns A hex string hash of the healthMetrics config
 */
export function computeHealthMetricsHash(settings: PreTradingSettings): string {
  const normalized = {
    source: settings.healthMetrics.source,
    metrics: [...settings.healthMetrics.metrics]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(m => ({
        id: m.id,
        name: m.name,
        min: m.min,
        max: m.max,
        unit: m.unit,
      })),
  };
  return djb2Hash(JSON.stringify(normalized));
}

/**
 * Computes a deterministic hash of personalPrepFields config for per-section ack tracking.
 * Uses stable ID ordering to ensure consistent hash regardless of array order.
 *
 * @param settings - The PreTradingSettings containing personalPrepFields to hash
 * @returns A hex string hash of the personalPrepFields config
 */
export function computePersonalPrepHash(settings: PreTradingSettings): string {
  const normalized = [...settings.personalPrepFields]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(f => ({
      id: f.id,
      name: f.name,
      type: f.type,
      description: f.description,
      options: f.options
        ? [...f.options].sort((a, b) => a.id.localeCompare(b.id)).map(o => ({
            id: o.id,
            label: o.label,
          }))
        : undefined,
    }));
  return djb2Hash(JSON.stringify(normalized));
}

/**
 * Computes a deterministic hash of sectionConfig (visibility & order) for per-section ack tracking.
 * Uses stable ID ordering to ensure consistent hash regardless of array order.
 *
 * @param settings - The PreTradingSettings containing sectionConfig to hash
 * @returns A hex string hash of the sectionConfig
 */
export function computeSectionVisibilityHash(settings: PreTradingSettings): string {
  // Normalize to full section set before hashing — ensures stable hash regardless of
  // whether the source is a normalized draft or un-normalized settings
  const normalized = normalizeSectionConfigForComparison(settings.sectionConfig)
    .sort((a, b) => a.sectionId.localeCompare(b.sectionId))
    .map(s => ({
      sectionId: s.sectionId,
      visible: s.visible,
      order: s.order,
    }));
  return djb2Hash(JSON.stringify(normalized));
}

/**
 * Computes a deterministic hash of finalCheckItems config for per-section ack tracking.
 * Uses stable ID ordering to ensure consistent hash regardless of array order.
 *
 * @param settings - The PreTradingSettings containing finalCheckItems to hash
 * @returns A hex string hash of the finalCheckItems config
 */
export function computeFinalCheckItemsHash(settings: PreTradingSettings): string {
  // Use empty array if undefined (matches mismatch detection behavior)
  const normalized = settings.finalCheckItems
    ? [...settings.finalCheckItems]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(item => ({
          id: item.id,
          label: item.label,
        }))
    : [];
  return djb2Hash(JSON.stringify(normalized));
}

/**
 * Computes a deterministic hash of MarketSnapshotSettings.snapshotFields for ack tracking.
 * Used to detect field configuration changes and suppress re-prompting after acknowledgment.
 *
 * Order-insensitive: sorts fields by id before hashing so field reordering doesn't
 * trigger mismatch detection. Only structural changes (add/remove/rename fields,
 * change options) should trigger mismatch.
 *
 * @param snapshotFields - The SnapshotFieldDefinition array from MarketSnapshotSettings
 * @returns A hex string hash of the field definitions
 */
export function computeMarketSnapshotFieldsHash(snapshotFields: SnapshotFieldDefinition[]): string {
  // Normalize fields: sort by id, include all structural fields
  const normalizedFields = [...snapshotFields]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(f => ({
      id: f.id,
      name: f.name,
      description: f.description,
      type: f.type,
      pinToLive: f.pinToLive,
      // For dropdown fields: sort options by id, include id and label
      options: f.options
        ? [...f.options].sort((a, b) => a.id.localeCompare(b.id)).map(o => ({
            id: o.id,
            label: o.label,
          }))
        : undefined,
    }));

  const jsonString = JSON.stringify(normalizedFields);
  return djb2Hash(jsonString);
}

/**
 * Computes a deterministic hash of TradingSetupSettings defaults for ack tracking.
 * Uses canonical normalization: sorts pairs by strategyId then playbookId,
 * sorts instrument IDs alphabetically, includes defaultAccountId.
 *
 * @param settings - The TradingSetupSettings to hash
 * @returns A hex string hash of the trading setup defaults
 */
export function computeTradingSetupHash(settings: TradingSetupSettings): string {
  const normalized = {
    pairs: [...settings.defaultStrategyPlaybookPairs]
      .sort(comparePairs)
      .map(p => ({
        strategyId: p.strategyId,
        playbookId: p.playbookId,
      })),
    accountId: settings.defaultAccountId,
    instrumentIds: [...settings.defaultFocusInstrumentIds].sort(),
  };
  return djb2Hash(JSON.stringify(normalized));
}

/**
 * Computes a deterministic hash of the effective default risk profile for an account.
 * Used to suppress re-prompting the user when they acknowledge a default profile change.
 *
 * @param effectiveDefaultProfileId - The resolved effective default profile ID
 *   (defaultProfileId if active, else first active profile, else undefined)
 * @returns A hex string hash of the effective default profile
 */
export function computeRiskProfileDefaultHash(effectiveDefaultProfileId: string | undefined): string {
  return djb2Hash(JSON.stringify({ profileId: effectiveDefaultProfileId ?? null }));
}

/**
 * Creates an empty SessionDraft with all required fields initialized.
 *
 * @param sessionType - The type of trading session ('live', 'paper', 'observation', 'replay')
 * @param date - The trading date in YYYY-MM-DD format (in user's display timezone)
 * @param settings - Optional PreTradingSettings to snapshot into the draft.
 *                   When provided, `preTradingConfig` is populated with a deep copy.
 *                   When omitted (tests/edge cases), `preTradingConfig` is undefined.
 * @param tradingSetupDefaults - Optional TradingSetupDefaults to pre-fill trading setup fields.
 *                               When provided, `activePlaybookSelections`, `defaultAccountId`,
 *                               and `focusInstrumentIds` are pre-filled from defaults.
 *                               `setupConfirmed` is always `false` (defaults never skip confirmation).
 * @returns A new SessionDraft ready for use
 */
export function createEmptySessionDraft(
  sessionType: SessionDraft['sessionType'],
  date: string,
  settings?: PreTradingSettings,
  tradingSetupDefaults?: TradingSetupDefaults
): SessionDraft {
  const isoTimestamp = new Date().toISOString();

  const metadata: DraftMetadata = {
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
    sections: {
      preTrading: {
        hasContent: false,
        isComplete: false,
      },
      objectives: {
        hasContent: false,
        needsAchievementStatus: false,
      },
      trades: {
        hasContent: false,
        incompleteCount: 0,
      },
      marketStates: {
        hasContent: false,
      },
      economicEvents: {
        hasContent: false,
        missingActuals: 0,
      },
      postTrading: {
        hasContent: false,
        isComplete: false,
      },
    },
    validationIssues: [],
    requiresReview: [],
    canClose: false,
  };

  const draft: SessionDraft = {
    id: generateUUID(),
    date,
    status: 'pre-trading',
    sessionType,
    objectives: [],
    trades: [],
    economicEvents: [],
    // marketStates intentionally omitted - optional field populated when instrument selected
    metadata,
    // phaseElapsedMs intentionally omitted - undefined means no accumulated time yet
    currentPhaseStartedAt: isoTimestamp,
    // Trading setup fields (WU7C) - always present
    activePlaybookSelections: [],
    setupConfirmed: false,
  };

  // Add config snapshot if settings provided
  if (settings) {
    draft.preTradingConfig = buildPreTradingConfigSnapshot(settings);
  }

  // Pre-fill trading setup from defaults if provided
  if (tradingSetupDefaults) {
    if (tradingSetupDefaults.defaultStrategyPlaybookPairs) {
      draft.activePlaybookSelections = tradingSetupDefaults.defaultStrategyPlaybookPairs.map(p => ({ ...p }));
    }
    if (tradingSetupDefaults.defaultAccountId) {
      draft.defaultAccountId = tradingSetupDefaults.defaultAccountId;
      draft.plannedAccountIds = [tradingSetupDefaults.defaultAccountId];
    }
    if (tradingSetupDefaults.defaultFocusInstrumentIds) {
      draft.focusInstrumentIds = [...tradingSetupDefaults.defaultFocusInstrumentIds];
    }
  }

  return draft;
}

/**
 * Creates an InstrumentSnapshot from an Instrument entity.
 * Captures instrument state at a point in time for historical accuracy.
 *
 * @param instrument - The Instrument entity to snapshot
 * @returns InstrumentSnapshot with all required fields
 */
export function buildInstrumentSnapshot(instrument: Instrument): InstrumentSnapshot {
  return {
    instrumentId: instrument.id,
    symbol: instrument.symbol,
    displayName: instrument.displayName,
    assetClass: instrument.classification,
    contractSize: instrument.contractSize,
    contractAsset: instrument.contractAsset,
    incrementSize: instrument.incrementSize,
    incrementType: instrument.incrementType,
    quoteCurrency: instrument.quoteCurrency,
  };
}

/**
 * Creates a fieldConfigSnapshot from SnapshotFieldDefinition array.
 * Deep copies all fields including dropdown options for draft independence.
 *
 * @param snapshotFields - Array of SnapshotFieldDefinition from MarketSnapshotSettings
 * @returns Deep-copied field config snapshot for MarketStateDraft
 */
export function buildFieldConfigSnapshot(
  snapshotFields: SnapshotFieldDefinition[]
): MarketStateDraft['fieldConfigSnapshot'] {
  return snapshotFields.map((f) => {
    const field: MarketStateDraft['fieldConfigSnapshot'][number] = {
      fieldId: f.id,
      name: f.name,
      type: f.type,
      pinToLive: f.pinToLive,
    };
    if (f.description !== undefined) field.description = f.description;
    if (f.options) field.options = f.options.map((o) => ({ ...o }));
    return field;
  });
}
